import React, { useState } from 'react';
import { Button, Typography, Tooltip } from 'antd';
import {
  GlobalOutlined,
  LogoutOutlined,
  DashboardOutlined,
  ShopOutlined,
  CarOutlined,
  CreditCardOutlined,
  DollarOutlined,
  TagOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  AuditOutlined,
  ShoppingOutlined,
  FileTextOutlined,
  ApartmentOutlined,
} from '@ant-design/icons';
import { useTheme } from '../../../tokens';

const { Text } = Typography;

interface SidebarContentProps {
  selectedKeys: string[];
  onMenuClick: (info: { key: string }) => void;
  onLogout: () => void;
}

interface NavItem {
  key: string;
  label: string;
  icon: React.ReactNode;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    title: 'OPERACIONES',
    items: [
      { key: '1', label: 'Panel de Control', icon: <DashboardOutlined /> },
      { key: '2', label: 'Gestión de Aliados', icon: <ShopOutlined /> },
      { key: '11', label: 'Órdenes', icon: <ShoppingOutlined /> },
      { key: '3', label: 'Proveedores de Delivery', icon: <CarOutlined /> },
    ],
  },
  {
    title: 'FINANZAS',
    items: [
      { key: '4', label: 'Pagos de Pedidos', icon: <CreditCardOutlined /> },
      { key: '16', label: 'Métodos de Pago', icon: <CreditCardOutlined /> },
      { key: '13', label: 'Facturas neteadas', icon: <FileTextOutlined /> },
      { key: '10', label: 'Comisiones', icon: <AuditOutlined /> },
      { key: '6', label: 'Reporte Financiero', icon: <DollarOutlined /> },
      { key: '12', label: 'KPIs de Delivery', icon: <CarOutlined /> },
    ],
  },
  {
    title: 'SUSCRIPCIONES',
    items: [
      { key: '8', label: 'Planes de Suscripción', icon: <TagOutlined /> },
      { key: '9', label: 'Suscripciones Activas', icon: <SafetyCertificateOutlined /> },
      { key: '15', label: 'Facturas de suscripción', icon: <FileTextOutlined /> },
    ],
  },
  {
    title: 'SISTEMA',
    items: [
      { key: '14', label: 'Industrias', icon: <ApartmentOutlined /> },
      { key: '5', label: 'Configuración Global', icon: <SettingOutlined /> },
    ],
  },
];

const NavButton: React.FC<{
  item: NavItem;
  isActive: boolean;
  t: any;
  onClick: () => void;
}> = ({ item, isActive, t, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Tooltip title={item.label} placement="right" mouseEnterDelay={0.5}>
      <Button
        type="text"
        block
        icon={item.icon}
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="sidebar-nav-item"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 10,
          height: 40,
          padding: '0 20px',
          margin: '2px 8px',
          width: 'calc(100% - 16px)',
          borderRadius: t.radiusSm,
          color: isActive ? t.primary : isHovered ? t.textPrimary : t.textSecondary,
          backgroundColor: isActive
            ? t.primaryMuted
            : isHovered
              ? `${t.textMuted}08`
              : 'transparent',
          fontWeight: isActive ? 500 : 400,
          fontSize: t.fontBody,
          borderLeft: isActive ? `2px solid ${t.primary}` : '2px solid transparent',
          transition: 'all 150ms cubic-bezier(0.23, 1, 0.32, 1)',
          transform: isHovered && !isActive ? 'translateX(2px)' : 'none',
        }}
      >
        {item.label}
      </Button>
    </Tooltip>
  );
};

export const SidebarContent: React.FC<SidebarContentProps> = ({
  selectedKeys,
  onMenuClick,
  onLogout,
}) => {
  const { t } = useTheme();

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo */}
      <div
        style={{
          height: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: `1px solid ${t.border}`,
          gap: 8,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: t.radiusSm,
            backgroundColor: t.primaryMuted,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <GlobalOutlined style={{ fontSize: 16, color: t.primary }} />
        </div>
        <Text
          strong
          style={{
            color: t.textPrimary,
            fontSize: 13,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontWeight: 600,
          }}
        >
          SAMA ADMIN
        </Text>
      </div>

      {/* Navigation */}
      <nav style={{ flex: 1, overflow: 'auto', padding: '12px 0' }}>
        {navSections.map((section) => (
          <div key={section.title} style={{ marginBottom: 8 }}>
            {/* Section Label */}
            <div
              style={{
                padding: '8px 20px 6px',
                fontSize: 10,
                fontWeight: 600,
                color: t.textMuted,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              {section.title}
            </div>

            {/* Items */}
            {section.items.map((item) => (
              <NavButton
                key={item.key}
                item={item}
                isActive={selectedKeys.includes(item.key)}
                t={t}
                onClick={() => onMenuClick({ key: item.key })}
              />
            ))}
          </div>
        ))}
      </nav>

      {/* Logout */}
      <div style={{ padding: '12px 8px', borderTop: `1px solid ${t.border}` }}>
        <Button
          type="text"
          danger
          icon={<LogoutOutlined />}
          onClick={onLogout}
          block
          style={{
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 10,
            padding: '0 20px',
            borderRadius: t.radiusSm,
            color: t.danger,
            backgroundColor: t.dangerMuted,
            fontWeight: 500,
            fontSize: t.fontBody,
            transition: 'all 150ms ease',
          }}
        >
          Cerrar sesión
        </Button>
      </div>
    </div>
  );
};

export default SidebarContent;
